/**
 * Resource Hints Configuration
 * Optimizes loading of external resources through preconnect and dns-prefetch
 */

export interface ResourceHint {
  url: string;
  type: 'preconnect' | 'dns-prefetch' | 'prefetch' | 'preload';
  crossorigin?: boolean;
  as?: 'script' | 'style' | 'font' | 'image' | 'document';
  importance?: 'high' | 'low' | 'auto';
}

// Configuration for external resource hints
export const resourceHints: ResourceHint[] = [
  // Note: This app uses @fontsource packages for fonts (bundled locally)
  // so no Google Fonts preconnect is needed
  
  // Auth provider endpoints (configured dynamically)
  // GraphQL endpoint (configured dynamically)
  // Sentry (if enabled)
];

/**
 * Get resource hints based on current configuration
 */
export function getResourceHints(): ResourceHint[] {
  const hints = [...resourceHints];
  
  // Add auth provider URL if external
  const authUrl = import.meta.env.VITE_AUTH_URL;
  if (authUrl && !authUrl.includes('localhost')) {
    try {
      const authOrigin = new URL(authUrl).origin;
      hints.push({
        url: authOrigin,
        type: 'preconnect',
      });
    } catch (e) {
      console.warn('Invalid auth URL:', authUrl);
    }
  }
  
  // Add GraphQL endpoint if external
  const graphqlEndpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT;
  if (graphqlEndpoint && !graphqlEndpoint.includes('localhost')) {
    try {
      const graphqlOrigin = new URL(graphqlEndpoint).origin;
      hints.push({
        url: graphqlOrigin,
        type: 'preconnect',
      });
    } catch (e) {
      console.warn('Invalid GraphQL endpoint:', graphqlEndpoint);
    }
  }
  
  // Add Sentry if enabled
  if (import.meta.env.VITE_ENABLE_SENTRY === 'true' && import.meta.env.VITE_SENTRY_DSN) {
    hints.push({
      url: 'https://browser.sentry-cdn.com',
      type: 'preconnect',
      crossorigin: true,
    });
  }
  
  return hints;
}

/**
 * Inject resource hint tags into document head
 */
export function injectResourceHints(): void {
  const hints = getResourceHints();
  const head = document.head;
  
  hints.forEach(hint => {
    // Check if hint already exists
    const selector = hint.type === 'preload' 
      ? `link[rel="${hint.type}"][href="${hint.url}"]`
      : `link[rel="${hint.type}"][href="${hint.url}"]`;
    
    if (document.querySelector(selector)) {
      return; // Skip if already exists
    }
    
    const link = document.createElement('link');
    link.rel = hint.type;
    
    if (hint.type === 'preload' || hint.type === 'prefetch') {
      link.href = hint.url;
      if (hint.as) link.as = hint.as;
      if (hint.importance) link.setAttribute('importance', hint.importance);
    } else {
      link.href = hint.url;
    }
    
    if (hint.crossorigin) {
      link.crossOrigin = 'anonymous';
    }
    
    head.appendChild(link);
  });
}

/**
 * Prefetch route chunks on hover/focus
 */
export function setupRoutePrefetching(): void {
  // Get all route links
  const routeLinks = document.querySelectorAll('a[href^="/"]');
  
  const prefetchedChunks = new Set<string>();
  
  const prefetchRoute = async (path: string) => {
    // Skip if already prefetched
    if (prefetchedChunks.has(path)) return;
    
    // Map routes to their chunk names (based on lazy loading setup)
    const routeChunkMap: Record<string, string> = {
      '/sftp-sync': 'feature-sftp',
      '/sites': 'feature-sites',
      '/users': 'feature-users',
      '/csv-import': 'feature-csv',
      '/on-hand-process': 'feature-onhand',
      '/settings': 'SettingsPage',
      '/logs': 'IterationsGrid',
    };
    
    // Find matching chunk
    const matchingRoute = Object.keys(routeChunkMap).find(route => 
      path.startsWith(route)
    );
    
    if (matchingRoute) {
      const chunkName = routeChunkMap[matchingRoute];
      prefetchedChunks.add(path);
      
      // Create prefetch link
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = `/assets/${chunkName}.js`; // Actual filename will have hash
      document.head.appendChild(link);
    }
  };
  
  // Add prefetch on hover/focus
  routeLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (!href) return;
    
    let prefetchTimer: NodeJS.Timeout;
    
    const startPrefetch = () => {
      prefetchTimer = setTimeout(() => {
        prefetchRoute(href);
      }, 100); // Small delay to avoid prefetching on quick hovers
    };
    
    const cancelPrefetch = () => {
      clearTimeout(prefetchTimer);
    };
    
    link.addEventListener('mouseenter', startPrefetch);
    link.addEventListener('focus', startPrefetch);
    link.addEventListener('mouseleave', cancelPrefetch);
    link.addEventListener('blur', cancelPrefetch);
  });
}

/**
 * Setup intersection observer for lazy prefetching
 */
export function setupLazyPrefetching(): void {
  if (!('IntersectionObserver' in window)) return;
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const link = entry.target as HTMLAnchorElement;
        const href = link.getAttribute('href');
        if (href && href.startsWith('/')) {
          // Prefetch route when link comes into viewport
          setupRoutePrefetching();
          observer.unobserve(link);
        }
      }
    });
  }, {
    rootMargin: '50px', // Start prefetching 50px before element is visible
  });
  
  // Observe all internal links
  document.querySelectorAll('a[href^="/"]').forEach(link => {
    observer.observe(link);
  });
}